import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
  EntityState,
  createSelector,
  PayloadAction,
} from '@reduxjs/toolkit';
import axios from 'axios';
import {
  AvailableAssignee,
  AssignmentStats,
  AssignmentStatsType,
  AvailableAssigneeWithAssignmentStats,
} from 'types/store/availableAssignees';
import { RootState, ThunkApi } from 'state/store';
import { fetchAvailableAssignees as fetchAvailableAssigneesApi } from 'api/availableAssignees';
import {
  getAvailableAssigneesUrl,
  deserializeAvailableAssignees,
  getAssigneeCheckedStatusFromAssignmentStats,
  getAssigneeInitials,
} from './utils';
import { Status } from 'utils/customTypes';
import { SLICE_STATUS } from 'utils/constants';
import { selectProjectTasksTableFilters } from '../../../projectTasksListSlice';
import { selectProjectTasksExportState } from '../../../export/exportSlice';

const availableAssigneesAdapter = createEntityAdapter<AvailableAssignee>();

interface AvailableAssigneesState {
  availableAssignees: EntityState<AvailableAssignee>;
  assignmentStatsMap?: Record<string, AssignmentStats>;
  links?: Record<string, string>;
  status?: Status;
  error?: string;
  isAssigneesListVisible?: boolean;
  isFetchingMore?: boolean;
}

const initialState: AvailableAssigneesState = {
  availableAssignees: availableAssigneesAdapter.getInitialState(),
  status: SLICE_STATUS.IDLE,
  isFetchingMore: false,
};

export interface FetchAvailableAssigneesParams {
  field?: string;
  projectId?: string;
  name?: string;
  limit?: number;
  includeDisabled?: boolean;
  includeAssignmentStats?: boolean;
}

export interface FetchAvailableAssigneesResponse {
  assignees: AvailableAssigneeWithAssignmentStats[];
  links: Record<string, string>;
  meta: Record<string, unknown>;
}

function fetchCondition(
  _thunkParameters: unknown,
  { getState }: { getState: () => RootState }
): boolean {
  const { status } =
    getState().projectTasksList.bulkActions.bulkAssign.availableAssignees;
  return status !== SLICE_STATUS.LOADING;
}

function fetchMoreCondition(
  _thunkParameters: unknown,
  { getState }: { getState: () => RootState }
): boolean {
  const { isFetchingMore, links } =
    getState().projectTasksList.bulkActions.bulkAssign.availableAssignees;
  return !isFetchingMore && !!links?.next;
}

export const fetchAvailableAssignees = createAsyncThunk<
  FetchAvailableAssigneesResponse,
  FetchAvailableAssigneesParams,
  ThunkApi
>(
  'projectTasksList/bulkActions/fetchAvailableAssignees',
  async (
    params: FetchAvailableAssigneesParams,
    { getState, rejectWithValue }
  ) => {
    try {
      const state = getState() as RootState;
      const filters = selectProjectTasksTableFilters(state);
      const exportState = selectProjectTasksExportState(state);
      const url = getAvailableAssigneesUrl({
        ...params,
        filters,
        exportState: {
          selectAll: exportState.selectAllTasks,
          excludedIds: exportState.excludedTasksIds,
          selectedIds: exportState.selectedTasksIds,
        },
      });
      const response = await fetchAvailableAssigneesApi({ url });
      return deserializeAvailableAssignees(response);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data);
      }
      return rejectWithValue('Error fetching available assignees');
    }
  },
  {
    condition: fetchCondition,
  }
);

export const fetchMoreAvailableAssignees = createAsyncThunk<
  FetchAvailableAssigneesResponse,
  void,
  ThunkApi
>(
  'projectTasksList/bulkActions/fetchMoreAvailableAssignees',
  async (_params, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const availableAssigneesState = selectAvailableAssigneesState(state);
      const url = availableAssigneesState.links?.next as string;
      const response = await fetchAvailableAssigneesApi({ url });
      return deserializeAvailableAssignees(response);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data);
      }
      return rejectWithValue('Error fetching more available assignees');
    }
  },
  {
    condition: fetchMoreCondition,
  }
);

const availableAssigneesSlice = createSlice({
  name: 'projectTasksList/bulkActions/bulkAssign/availableAssignees',
  initialState,
  reducers: {
    setIsAssigneesListVisible: (state, action: PayloadAction<boolean>) => {
      state.isAssigneesListVisible = action.payload;
      if (!action.payload) {
        state.availableAssignees = availableAssigneesAdapter.getInitialState();
        state.assignmentStatsMap = undefined;
      }
    },
    resetAvailableAssignees: (state) => {
      state.availableAssignees = availableAssigneesAdapter.getInitialState();
      state.assignmentStatsMap = undefined;
    },
  },
  extraReducers: (builder) => {
    // Fetch available assignees (initial fetch / search)
    builder.addCase(fetchAvailableAssignees.pending, (state) => {
      state.status = SLICE_STATUS.LOADING;
    });
    builder.addCase(fetchAvailableAssignees.fulfilled, (state, action) => {
      state.status = SLICE_STATUS.SUCCESS;
      if (!action.payload) {
        return;
      }
      const { assignees, links } = action.payload;
      const availableAssignees: AvailableAssignee[] = [];
      const assignmentStatsMap = new Map<string, AssignmentStats>();
      for (const assignee of assignees) {
        const { assignmentStats, ...assigneeData } = assignee;
        availableAssignees.push(assigneeData);
        assignmentStatsMap.set(assigneeData.id, assignmentStats);
      }
      availableAssigneesAdapter.setAll(
        state.availableAssignees,
        availableAssignees
      );
      state.assignmentStatsMap = Object.fromEntries(
        assignmentStatsMap.entries()
      );
      state.links = links;
    });
    builder.addCase(fetchAvailableAssignees.rejected, (state, action) => {
      state.status = SLICE_STATUS.FAILED;
      state.error = action.error.message;
    });

    // Fetch more available assignees (infinite scroll)
    builder.addCase(fetchMoreAvailableAssignees.pending, (state) => {
      state.isFetchingMore = true;
    });
    builder.addCase(fetchMoreAvailableAssignees.fulfilled, (state, action) => {
      state.isFetchingMore = false;
      if (!action.payload) {
        return;
      }
      const { assignees, links } = action.payload;
      const availableAssignees: AvailableAssignee[] = [];
      const assignmentStatsMap = new Map<string, AssignmentStats>();
      for (const assignee of assignees) {
        const { assignmentStats, ...assigneeData } = assignee;
        availableAssignees.push(assigneeData);
        assignmentStatsMap.set(assigneeData.id, assignmentStats);
      }
      availableAssigneesAdapter.upsertMany(
        state.availableAssignees,
        availableAssignees
      );
      state.assignmentStatsMap = {
        ...state.assignmentStatsMap,
        ...Object.fromEntries(assignmentStatsMap.entries()),
      };
      state.links = links;
    });
    builder.addCase(fetchMoreAvailableAssignees.rejected, (state, action) => {
      state.isFetchingMore = false;
      state.error = action.error.message;
    });
  },
});

const selectAvailableAssigneesState = (state: RootState) =>
  state.projectTasksList.bulkActions.bulkAssign.availableAssignees;

const { selectAll: selectAllAvailableAssignees } =
  availableAssigneesAdapter.getSelectors(
    (state: RootState) =>
      selectAvailableAssigneesState(state).availableAssignees
  );

const selectAvailableAssigneesStatus = createSelector(
  [selectAvailableAssigneesState],
  (availableAssigneesState) => availableAssigneesState.status
);

export const selectIsFetchingAvailableAssignees = createSelector(
  [selectAvailableAssigneesStatus],
  (status) => status === SLICE_STATUS.LOADING
);

export const selectIsFetchingMoreAvailableAssignees = createSelector(
  [selectAvailableAssigneesState],
  (availableAssigneesState) => availableAssigneesState.isFetchingMore
);

export const selectAssignmentStatsMap = createSelector(
  [selectAvailableAssigneesState],
  (availableAssigneesState) => availableAssigneesState.assignmentStatsMap
);

export const selectIsAssigneesListVisible = createSelector(
  [selectAvailableAssigneesState],
  (availableAssigneesState) => availableAssigneesState.isAssigneesListVisible
);

export const selectAvailableAssigneesWithCheckedStatus = createSelector(
  [selectAllAvailableAssignees, selectAssignmentStatsMap],
  (availableAssignees, assignmenStatsMap) => {
    if (!availableAssignees.length) {
      return [];
    }
    return availableAssignees.map((assignee: AvailableAssignee) => {
      const assignmentStats = assignmenStatsMap?.[assignee.id];
      const { name, id, avatarUrl, ...assigneeData } = assignee;
      return {
        ...assigneeData,
        label: name,
        value: id,
        avatar: {
          imageSrc: avatarUrl,
          name,
          initial: getAssigneeInitials(assignee),
        },
        checked: getAssigneeCheckedStatusFromAssignmentStats(
          assignmentStats?.assignedTo ?? AssignmentStatsType.NONE
        ),
      };
    });
  }
);

export const selectCanFetchMoreAvailableAssignees = createSelector(
  [selectAvailableAssigneesState],
  (availableAssigneesState) =>
    availableAssigneesState.links?.next ? true : false
);

export const { setIsAssigneesListVisible, resetAvailableAssignees } =
  availableAssigneesSlice.actions;

export default availableAssigneesSlice.reducer;
